import { useCallback, useEffect, useMemo, useState } from "react";

import { type ChartData, type ChartDataset } from "chart.js";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlideAtom } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  KpiType,
  type ModelMetadataKpiFormula,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { curateHistoricalDataset } from "../../model-fit-chart/utils/export-to-csv";
import { type DataColumn } from "../incremental-spend-chart/const";

import { getChartOptions } from "./chart-options";
import {
  PROMO_BORDER_DASH,
  PROMO_COLOR,
  PROMO_ID,
  REVENUE_COLOR,
  REVENUE_ID,
  SPEND_BORDER_DASH,
  ROAS_COLOR,
  ROAS_ID,
  SPEND_COLOR,
  SPEND_ID,
  TOTAL_KPI_BORDER_DASH,
  TOTAL_KPI_COLOR,
  TOTAL_KPI_ID,
} from "./const";
import { Legend } from "./legend";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";
import { setYAxesTitles } from "./plugins/y-scale-titles-plugin";

interface Props {
  columnName: string;
  historicalContributionsData: any;
  selectedKpi: ModelMetadataKpiFormula;
  column: DataColumn;
  dateRange: string;
}

export const IncrementalRoasChart = ({
  historicalContributionsData,
  columnName,
  selectedKpi,
  column,
  dateRange,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();

  const { user } = useUser();

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined | null) => {
      if (instance) {
        setChartRef(instance);
      }
    },
    [],
  );

  const [datasetVisibility, setDatasetVisibility] = useState<
    Record<string, boolean>
  >({
    [SPEND_ID]: true,
    [TOTAL_KPI_ID]: true,
    [REVENUE_ID]: false,
    [PROMO_ID]: false,
    [ROAS_ID]: false,
  });

  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  const makeDerivativeKpiDataset = useCallback(
    (data: number[]): ChartDataset<"line"> => ({
      yAxisID: ROAS_ID,
      label: isRevenue ? "Incremental ROAS" : "Incremental CAC",
      type: "line",
      backgroundColor: ROAS_COLOR,
      borderColor: ROAS_COLOR,
      tension: 0.2,
      pointRadius: 0,
      data,
    }),
    [isRevenue],
  );

  const data = useMemo<ChartData<any>>(() => {
    const rows = historicalContributionsData[columnName];

    const labels: string[] = [];
    const kpiData: number[] = [];
    const promoData: number[] = [];
    const spendData: number[] = [];
    const totalKpi: number[] = [];
    const efficiencyMetricData: number[] = [];

    const rowsToUse = curateHistoricalDataset(rows, dateRange);
    rowsToUse.forEach((row: any) => {
      const kpiValue = row.kpiData[selectedKpi.kpi_name];
      const promoValue = row.promoData[selectedKpi.kpi_name];
      const startDate = moment(row.date);
      const endDate = moment(startDate).add(6, "days");
      labels.push(
        rowsToUse.length > 10
          ? `${startDate.format("MMM D, YYYY")}`
          : `${startDate.format("MMM D, YYYY")} - ${endDate.format(
              "MMM D, YYYY",
            )}`,
      );
      kpiData.push(kpiValue);
      promoData.push(promoValue);
      totalKpi.push(kpiValue + promoValue);
      spendData.push(row.spend);
      efficiencyMetricData.push(
        isRevenue ? kpiValue / row.spend : row.spend / kpiValue,
      );
    });

    return {
      labels,
      datasets: [
        {
          backgroundColor: TOTAL_KPI_COLOR,
          borderColor: TOTAL_KPI_COLOR,
          data: totalKpi,
          animation: false,
          label: `${selectedKpi.friendly_name} (promos + channel contribution)`,
          type: "line",
          yAxisID: REVENUE_ID,
          id: TOTAL_KPI_ID,
          borderDash: TOTAL_KPI_BORDER_DASH,
          tension: 0.2,
          pointRadius: 0,
          order: 2,
        },
        {
          backgroundColor: PROMO_COLOR,
          borderColor: PROMO_COLOR,
          data: promoData,
          animation: false,
          label: `${selectedKpi.friendly_name} (promotion contribution only)`,
          type: "line",
          yAxisID: REVENUE_ID,
          borderDash: PROMO_BORDER_DASH,
          id: PROMO_ID,
          tension: 0.2,
          pointRadius: 0,
          order: 3,
        },
        {
          backgroundColor: REVENUE_COLOR,
          borderColor: REVENUE_COLOR,
          data: kpiData,
          animation: false,
          label: `${selectedKpi.friendly_name} (channel contribution only)`,
          type: "line",
          yAxisID: REVENUE_ID,
          id: REVENUE_ID,
          tension: 0.2,
          pointRadius: 0,
          order: 4,
        },
        {
          backgroundColor: SPEND_COLOR,
          borderColor: SPEND_COLOR,
          data: spendData,
          animation: false,
          label: "Spend",
          type: "line",
          yAxisID: SPEND_ID,
          borderDash: SPEND_BORDER_DASH,
          id: SPEND_ID,
          tension: 0.2,
          pointRadius: 0,
          order: 5,
        },
        {
          ...makeDerivativeKpiDataset(efficiencyMetricData),
          id: ROAS_ID,
          order: 1,
          animation: false,
        },
      ],
    };
  }, [
    historicalContributionsData,
    columnName,
    dateRange,
    selectedKpi.friendly_name,
    selectedKpi.kpi_name,
    makeDerivativeKpiDataset,
    isRevenue,
  ]);

  const chartOptions = useMemo(
    () => getChartOptions(user, selectedKpi.kpi_type),
    [user, selectedKpi.kpi_type],
  );

  useEffect(() => {
    if (chartRef) {
      setChartData(chartRef, { data, datasetVisibility });
      setYAxesTitles({
        chart: chartRef,
        naming: {
          [ROAS_ID]: isRevenue ? "Incremental ROAS" : "Incremental CAC",
          [REVENUE_ID]: selectedKpi.friendly_name,
          [SPEND_ID]: "Spend",
        },
      });
    }
  }, [chartRef, data, datasetVisibility, isRevenue, selectedKpi.friendly_name]);

  if (!historicalContributionsData[columnName]) {
    return (
      <div className="flex-center h-[450px]">
        <LoadingSlideAtom />
      </div>
    );
  }

  return (
    <div>
      <div className="font-bold text-lg px-5 py-2 pb-3">
        <span>Incremental performance over time </span>
        <GenericTooltip
          content={
            <div className="w-[15rem] text-subtitle text-xs font-normal">
              This graph allows you to see how your {column.label} incremental{" "}
              {isRevenue ? "ROAS" : "CAC"} has changed over time, and the
              contributions {column.label} had on {selectedKpi.friendly_name}.
            </div>
          }
        />
      </div>
      <Legend
        selectedKpi={selectedKpi}
        datasetVisibility={datasetVisibility}
        setDatasetVisibility={(value) => {
          trackMMMEvent({
            component: ModelInsightsTrackedComponent.INCREMENTAL_ROAS_CHART,
            action: "onChange",
            actionInfo: "Change dataset visibility",
          });

          setDatasetVisibility(value);
        }}
      />
      <div className="relative">
        <Line
          ref={setChartInstance}
          options={chartOptions}
          data={data}
          height={80}
          plugins={plugins}
        />
      </div>
    </div>
  );
};
